// @ts-nocheck
import React, { useState } from 'react';
import { Tent } from 'lucide-react';

import { NamedLink } from '../../../components';

interface CampScoutSectionProps {
  sectionId: string;
  sectionData?: any[];
  [key: string]: any;
}

const plannerSteps = [
  {
    number: 1,
    title: "Pick Your Camps",
    image: "/campscout-step-1.png",
    alt: "Select from activities like Soccer Camp, Art Camp, STEM Lab, and Theater Camp",
    description: "Don't worry about matching open sessions to your schedule. Just pick the camps your kids want to attend, and we'll handle the rest. Mix and match to build the perfect summer.",
  },
  {
    number: 2,
    title: "Set Your Preferences",
    image: "/campscout-step-2.png",
    alt: "Weekly calendar with preferences for avoiding overlaps, full week sessions, and morning times",
    description: "Our AI will plan your camps around your vacations. Just tell us your family's schedule and we'll match the sessions that fit when you can actually attend!",
  },
  {
    number: 3,
    title: "Get Your Optimized Plan",
    image: "/campscout-step-3.png",
    alt: "Complete summer camp schedule with weekly breakdown and schedule summary",
    description: "We generate an optimized, conflict-free summer plan. See every week mapped out with confirmed camps, drop-off and pick-up times, and a downloadable schedule at a glance. Book the whole summer in one go!",
  },
];

const CampScoutSection: React.FC<CampScoutSectionProps> = ({
  sectionId,
  sectionData,
  ...extraProps
}) => {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <section id="kamp-planner" className="bg-muted/30 scroll-mt-20">
      <div className="container px-4 md:px-6 py-16 lg:py-24">
        <div className="flex w-full flex-col items-center justify-center text-center mb-12">
          <p className="pb-3 font-bold text-destructive text-sm tracking-wide uppercase">CampScout</p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold">
            Plan the Perfect <span className="text-primary underline underline-offset-4">Summer</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto font-medium mt-3">
            Three simple steps to build a conflict-free summer camp schedule for your kids.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-4 items-start max-w-5xl mx-auto">
          <div className="flex flex-col gap-3 lg:w-80 shrink-0">
            {plannerSteps.map((step, idx) => (
              <button
                key={step.number}
                onMouseEnter={() => setActiveStep(idx)}
                onClick={() => setActiveStep(idx)}
                className={`flex items-start gap-4 rounded-2xl p-5 text-left transition-all duration-300 ${
                  activeStep === idx
                    ? "bg-background border-2 border-primary box-shadow-card"
                    : "bg-background/50 border-2 border-transparent hover:bg-background hover:border-border"
                }`}
                data-testid={`planner-step-${step.number}`}
              >
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold transition-colors duration-300 ${
                  activeStep === idx
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                }`}>
                  {step.number}
                </span>
                <div>
                  <h4 className={`font-bold text-base transition-colors duration-300 ${
                    activeStep === idx ? "text-primary" : "text-foreground"
                  }`}>
                    {step.title}
                  </h4>
                  <p className={`text-sm mt-1 transition-all duration-300 overflow-hidden ${
                    activeStep === idx
                      ? "max-h-40 opacity-100 text-muted-foreground"
                      : "max-h-0 opacity-0"
                  }`}>
                    {step.description}
                  </p>
                </div>
              </button>
            ))}

            <NamedLink name="SearchPage" className="mt-2">
              <span className="servibe-btn servibe-btn-primary inline-flex items-center gap-2 px-8 py-3 text-sm w-full justify-center" data-testid="button-try-planner">
                <span className="flex items-center gap-2">
                  <Tent className="h-4 w-4" />
                  Try CampScout
                </span>
              </span>
            </NamedLink>
          </div>

          <div className="flex-1 relative flex items-center justify-center min-h-[300px] lg:min-h-[400px]">
            {plannerSteps.map((step, idx) => (
              <img
                key={step.number}
                src={step.image}
                alt={step.alt}
                className={`w-full max-w-2xl rounded-2xl box-shadow-float transition-all duration-500 ${
                  activeStep === idx
                    ? "opacity-100 scale-100"
                    : "opacity-0 scale-95 absolute pointer-events-none"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CampScoutSection;
